<template>
  <div
    class="fixed-footer"
  >
    <Transition
      name="expand-fade"
      @before-enter="(el) => el.style.height = '0'"
      @enter="(el) => el.style.height = el.scrollHeight + 'px'"
      @after-enter="(el) => el.style.height = ''"
      @before-leave="(el) => el.style.height = el.scrollHeight + 'px'"
      @leave="(el) => el.style.height = '0'"
    >
      <div
        v-if="isExpanded"
        class="expand-content"
      >
        <div class="item">
          <div class="label">
            Total Items({{ estimatedQuantity }})
          </div>
          <div class="value">
            <IntlCurrency :currency="currencyData.targetCurrency" />
            {{ totalItemsAmount.toFixed(2) }}
          </div>
        </div>
        <div
          class="item"
          style="margin-bottom: 0;"
        >
          <div class="label">
            {{ shippingConfirmed ? 'Shipping Cost' : 'Est.Shipping' }}
          </div>
          <div class="value">
            <template v-if="!shippingConfirmed">
              Need a Quote
            </template>
            <template v-else>
              <IntlCurrency
                :currency="currencyData.targetCurrency"
              />
              {{ shipAmount.toFixed(2) }}
            </template>
          </div>
        </div>
      </div>
    </Transition>
    <div class="summary-info">
      <div class="summary-l">
        <div class="item">
          <div class="label total-cost">
            {{ shippingConfirmed ? 'Total Cost' : 'Est.Total Cost' }}
          </div>
          <div
            class="value total-cost"
            style="color: #052784; padding-right: 4px; font-size: 18px;"
          >
            <IntlCurrency :currency="currencyData.targetCurrency" />
            {{ totalAmount.toFixed(2) }}
          </div>
        </div>
        <div class="item">
          <div class="label pifp">
            {{ shippingConfirmed ? 'Per-Item Fulfillment Price' : 'Est.Per-Item Fulfillment Price' }}
          </div>
          <div class="value pifp">
            <IntlCurrency :currency="currencyData.targetCurrency" />
            {{ pifpPrice }}
          </div>
        </div>
      </div>
      <Icon
        @click="isExpanded = !isExpanded"
        :class="{ 'expanded': isExpanded }"
        class="expand-icon"
        name="arrow-down-l-2"
      />
    </div>
    <Footer
      ref="footerRef"
      :favorite="data?.collect"
    />
    <Processing
      v-model="processingVisible"
      :expand="isExpanded"
    />
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { Icon } from '@/components/Icon'
import { IntlCurrency } from '@/components/NewIntlCurrency'
import { useCurrencyStore } from '@/pinia/currency'
import Processing from './Processing.vue'
import Footer from '../Footer.vue'
import { useCalcShip } from '../../../hooks/useCalcShip'
const props = defineProps({
  data: {
    type: Object,
    default: () => ({})
  },
  isProcessing: {
    type: Boolean,
    default: false
  }
})

const footerRef = ref(null)

const currencyStore = useCurrencyStore()

const isExpanded = ref(false)

const processingVisible = ref(props.isProcessing)

const currencyData = computed(() => currencyStore.current)

const { selectedSkuList, shippingConfirmed, shipAmount, totalItemsAmount, totalAmount } = useCalcShip()

const estimatedQuantity = computed(() => {
  if (selectedSkuList.value.length === 0) return 0
  return selectedSkuList.value.reduce((pre, cur) => {
    return pre + cur.quantity
  }, 0)
})

const pifpPrice = computed(() => {
  if (!totalAmount.value) return 0

  const val = (totalItemsAmount.value + shipAmount.value) / estimatedQuantity.value
  return val.toFixed(2)
})

watch(() => props.isProcessing, (value) => {
  processingVisible.value = value
})

defineExpose({
  footerRef
})
</script>

<style lang='less' scoped>
.fixed-footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  backdrop-filter: blur(10px);
  border-top: 1px solid #EBF1F5;
  box-sizing: border-box;
  z-index: 99;

  .expand-content {
    background-color: white;
    padding: 12px 28px 0 12px;
    overflow: hidden;
  }

  .summary-info {
    position: relative;
    display: flex;
    margin-top: 12px;
    padding: 0 12px;
    z-index: 9;

    .summary-l {
      flex: 1;
    }

    .expand-icon {
      position: relative;
      top: 3px;
      transition: transform .3s;
      &.expanded {
        transform: rotate(180deg);
      }
    }
  }

  .summary {
    position: relative;
    display: flex;
    margin-top: 16px;
    padding: 0 12px;

    .summary-l {
      flex: 1;
    }

    .expand-icon {
      position: relative;
      top: 0;
      right: 0;
      transition: all 0.3s;
      &.expanded {
        transform: rotate(180deg);
      }
    }
  }

  .item {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;

    .label {
      color: #232323;
      font-size: 12px;
      font-family: mon-500;
      &.total-cost {
        font-size: 14px;
        font-family: mon-700;
      }
      &.pifp {
        color: #168A73;
        font-size: 11px;
        font-family: mon-600;
      }
    }
    .value {
      display: flex;
      align-items: center;
      color: #232323;
      font-size: 12px;
      font-family: mon-600;
      &.total-cost {
        font-size: 14px;
        font-family: mon-700;
      }
      &.pifp {
        color: #168A73;
        font-size: 11px;
        font-family: mon-700;
      }
    }
  }
}

.expand-fade-enter-active,
.expand-fade-leave-active {
  transition: height 0.2s ease-in-out;
}
</style>
